import React, { useState } from "react";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  useTheme,
  Divider,
  Tooltip,
} from "@mui/material";
import { tokens } from "../../../theme/theme";
import { EXCEL_API_ENDPOINTS } from "../../../config/api/apiCallExcel";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import { API_ENDPOINTS } from "../../../config/api/apiCall";
import ModuleHelpDialog from "../../../components/ModuleHelpDialog";
import { ordermanagementhelp } from "../../../data/ordermanagementhelp";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import HelpIcon from '@mui/icons-material/HelpOutline';
// 🔹 Excel serial → JS Date
const excelSerialToDate = (serial) => {
  if (!serial || isNaN(serial)) return serial;
  const parsed = XLSX.SSF.parse_date_code(serial);
  if (!parsed) return serial;
  return new Date(parsed.y, parsed.m - 1, parsed.d);
};

// 🔹 JS Date → dd-MMM-yyyy
const formatDateToDDMMMYYYY = (date) => {
  if (!(date instanceof Date) || isNaN(date)) return date;
  const day = String(date.getDate()).padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export default function UploadOrderIndex({ handleBackWinderMachineEntry }) {
  const navigate = useNavigate();
  const [excelData, setExcelData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [columns, setColumns] = useState([]);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isSaved, setIsSaved] = useState(false);


  const [helpOpen, setHelpOpen] = useState(false);
      const handleCloseHelp = () => setHelpOpen(false);
      const handleHelpMenuClick = () => setHelpOpen(true);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const workbook = XLSX.read(bstr, { type: "binary", cellDates: true });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      let json = XLSX.utils.sheet_to_json(worksheet, { defval: null, raw: true });

      // 🔍 Detect date headers
      const dateCols = new Set();
      for (const cell in worksheet) {
        if (cell[0] === "!") continue;
        const cellObj = worksheet[cell];
        if (cellObj.t === "n" && cellObj.z && cellObj.z.includes("d")) {
          const colLetter = cell.replace(/[0-9]/g, "");
          const colIndex = colLetter.charCodeAt(0) - 65;
          const header = json[0] ? Object.keys(json[0])[colIndex] : null;
          if (header) dateCols.add(header);
        }
      }

      // ✅ Convert only date columns
      json = json.map((row) => {
        const newRow = { ...row };
        [...dateCols].forEach((col) => {
          if (row[col] && typeof row[col] === "number") {
            newRow[col] = excelSerialToDate(row[col]);
          }
        });
        return newRow;
      });

      setExcelData(json);
      setIsSaved(false); // ✅ Reset save state on new file upload
      setValidationResults([]);
      setColumns(Object.keys(json[0] || {}));
    };
    reader.readAsBinaryString(file);
  };

  const handleValidate = async () => {
    if (excelData.length === 0) {
      alert("Please upload a file before validating.");
      return;
    }
    try {
      setLoading(true);

      // 🔹 Send formatted dates for validation
      const formattedData = excelData.map((row) => {
        const newRow = { ...row };
        Object.keys(newRow).forEach((key) => {
          if (newRow[key] instanceof Date) {
            newRow[key] = formatDateToDDMMMYYYY(newRow[key]);
          }
        });
        return newRow;
      });

      const response = await fetch(
        EXCEL_API_ENDPOINTS.ORDERMANAGEMENT_VALIDATE_EXCEL_BULK_UPLOAD,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formattedData),
        }
      );

      if (!response.ok) throw new Error("Validation failed");

      const result = await response.json();
      setValidationResults(result);
    } catch (error) {
      alert("Validation failed. Please try again.");
      console.error("Validation error:", error);
    } finally {
      setLoading(false);
    }
  };

  const allValid =
    validationResults.length > 0 &&
    validationResults.every((row) => row.Status === "Success");

  const handleSave = async () => {
    if (excelData.length === 0) {
      alert("No data to save.");
      return;
    }

    try {
      setLoading(true);

      // 🔹 Merge validation + format dates before save
      const finalDataToSave = excelData.map((row, idx) => {
        const validation = validationResults[idx] || {};
        const newRow = { ...row, ...validation };

        Object.keys(newRow).forEach((key) => {
          if (newRow[key] instanceof Date) {
            newRow[key] = formatDateToDDMMMYYYY(newRow[key]);
          }
        });

        return newRow;
      });

      const response = await fetch(
        EXCEL_API_ENDPOINTS.ORDERMANAGEMENT_SAVE_EXCEL_INDENT_DETAILS,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(finalDataToSave),
        }
      );

      if (!response.ok) throw new Error("Save failed");

      await response.json();
      alert("Orders saved successfully!");
       setIsSaved(true); // ✅ Disable Save button
    } catch (error) {
      alert("Save failed. Please try again.");
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  const validationColumns =
    validationResults.length > 0
      ? Array.from(
          new Set(
            validationResults.flatMap((row) =>
              Object.keys(row).filter((k) => k !== "Status" && k !== "Errors")
            )
          )
        )
      : [];

  return (
   <Box p={2} display="flex" flexDirection="column" height="200px">
    
      {/* Header with Close Icon */}
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <IconButton
         onClick={handleBackWinderMachineEntry} // 👈 update route if needed
          sx={{
            backgroundColor: "#theme.palette.secondary.main",
            "&:hover": { backgroundColor:theme.palette.secondary.main },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Typography variant="h6" gutterBottom>
        Upload Orders
      </Typography>

     

    <Box p={2}>
      <Typography variant="h6" gutterBottom>
        Upload Orders
      </Typography>

      {/* Buttons Row */}
      <Box display="flex" alignItems="center" gap={2} mb={2}>
        <label
          htmlFor="file-input"
          style={{ display: "flex", alignItems: "center", gap: 8 }}
        >
          <input
            id="file-input"
            type="file"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          <Tooltip title="Upload Excel">
            <IconButton component="span" color="secondary">
              <FileUploadRoundedIcon />
            </IconButton>
          </Tooltip>
          <Typography
            variant="body1"
            sx={{ color: theme.palette.secondary.main, cursor: "pointer" }}
          >
            Select Excel File to Upload
          </Typography>
        </label>

        {fileName && (
          <Typography variant="body2" sx={{ fontStyle: "italic" }}>
            File: {fileName}
          </Typography>
        )}

        <Button
          variant="contained"
          color="secondary"
          onClick={handleValidate}
          disabled={loading || excelData.length === 0}
        >
          Validate
        </Button>

        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          //disabled={!allValid || loading}
           disabled={!allValid || loading || isSaved} // ✅ disable after save
          startIcon={<SaveOutlinedIcon />}
        >
          Save
        </Button>

        <Button
          variant="outlined"
          color="warning"
          startIcon={<FileDownloadRoundedIcon />}
          onClick={() =>
            (window.location.href =
              "/excel-templates/Template_OrderBulkUpload.xlsx")
          }
        >
          Template
        </Button>

        <Tooltip title="View help document details" arrow>
                        <IconButton
                          size="small"
                          aria-label="help"
                          sx={{ mx: 1 }}
                          onClick={handleHelpMenuClick}
                        >
                          <HelpIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      
                      
                                              <ModuleHelpDialog
                                                  open={helpOpen}
                                                  onClose={handleCloseHelp}
                                                  data={ordermanagementhelp.BulkOrderUpload}
                                              />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Table */}
      {excelData.length > 0 && (
        <Box
          sx={{
            border: 1,
            borderColor: theme.palette.secondary.main,
            borderRadius: 2,
            overflow: "auto",
            maxHeight: "65vh",
            maxWidth: "100%",
          }}
        >
          <TableContainer>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.secondary.main }}>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      color: theme.palette.secondary.contrastText,
                      fontWeight: "bold",
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      color: theme.palette.secondary.contrastText,
                      fontWeight: "bold",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    sx={{
                      whiteSpace: "nowrap",
                      color: theme.palette.secondary.contrastText,
                      fontWeight: "bold",
                    }}
                  >
                    Validation Remarks
                  </TableCell>

                  {columns.map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        whiteSpace: "nowrap",
                        color: theme.palette.secondary.contrastText,
                        fontWeight: "bold",
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}

                  {validationColumns.map((col, i) => (
                    <TableCell
                      key={i}
                      sx={{
                        whiteSpace: "nowrap",
                        color: theme.palette.secondary.contrastText,
                        fontWeight: "bold",
                      }}
                    >
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {excelData.map((row, idx) => {
                  const validation = validationResults[idx] || {};

                  return (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{validation.Status || "Not Validated"}</TableCell>
                      <TableCell>
                        {validation.Errors?.length
                          ? validation.Errors.join("; ")
                          : ""}
                      </TableCell>

                      {columns.map((col, i) => (
                        <TableCell key={i}>
                          {row[col] instanceof Date
                            ? row[col].toLocaleDateString("en-CA") // UI shows yyyy-mm-dd
                            : row[col]}
                        </TableCell>
                      ))}

                      {validationColumns.map((col, i) => (
                        <TableCell key={i}>{validation[col] ?? ""}</TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
   {/* ✅ keep your existing upload form / table / logic here */}
      <Box flexGrow={1}></Box>
    </Box>
  );
}
